import { Plus, Trash2 } from 'lucide-react';
import { describeFormCondition, formOpLabel, opsForType, type FormAtom, type FormCondition, type FormGroup, type FormOp } from '@kaba/shared';
import { Button, cx } from '../../ui/ui';
import type { Def, Ref } from './model';

const sel = 'min-h-11 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2 text-base sm:min-h-9 sm:text-sm';

/**
 * Bedingungen als Sätze (R01): Feld → Vergleich → Wert; „Alle“ oder „Mindestens eine“; eine weitere Ebene als Gruppe.
 * `refs` sind nur frühere Fragen (R03) – Vorwärtsbezüge sind damit gar nicht wählbar.
 */
export function ConditionEditor({ def, refs, value, onChange, label }: { def: Def; refs: Ref[]; value: FormCondition | null | undefined; onChange: (c: FormCondition | null) => void; label: string }) {
  if (!refs.length) return <p className="text-xs text-[var(--ink-soft)]">Bedingungen können sich nur auf Fragen beziehen, die davor stehen. Hier gibt es noch keine.</p>;
  const c = value ?? null;
  if (!c || !c.items.length)
    return (
      <Button type="button" size="sm" onClick={() => onChange({ mode: 'all', items: [newAtom(refs[refs.length - 1]!)] })}>
        <Plus size={14} /> {label}
      </Button>
    );
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-[var(--line)] p-2">
      <GroupEditor refs={refs} g={c} onChange={(g) => onChange(g.items.length ? g : null)} depth={0} />
      <p className="rounded-md bg-[var(--column)] p-2 text-xs">
        <span className="font-medium">Zusammenfassung:</span> {describeFormCondition(def, c)}
      </p>
    </div>
  );
}

function newAtom(r: Ref): FormAtom {
  const op = opsForType(r.type)[0]!;
  const value = r.q?.type === 'choice' || r.q?.type === 'multichoice' ? (r.q.options?.[0]?.id ?? null) : r.type === 'yesno' || r.type === 'confirm' ? true : r.type === 'section' ? 1 : null;
  return { q: r.id, op: r.type === 'section' ? 'gte' : op, value };
}

function GroupEditor({ refs, g, onChange, depth }: { refs: Ref[]; g: FormGroup; onChange: (g: FormGroup) => void; depth: number }) {
  const set = (i: number, it: FormAtom | FormGroup | null) => onChange({ ...g, items: it ? g.items.map((x, j) => (j === i ? it : x)) : g.items.filter((_, j) => j !== i) });
  return (
    <div className={cx('flex flex-col gap-2', depth > 0 && 'rounded-md border border-dashed border-[var(--line)] p-2')}>
      <label className="flex items-center gap-2 text-sm">
        <select className={sel} value={g.mode} onChange={(e) => onChange({ ...g, mode: e.target.value as 'all' | 'any' })} aria-label="Verknüpfung">
          <option value="all">Alle treffen zu</option>
          <option value="any">Mindestens eine trifft zu</option>
        </select>
      </label>
      {g.items.map((it, i) =>
        'q' in it ? (
          <AtomEditor key={i} refs={refs} a={it} onChange={(a) => set(i, a)} />
        ) : (
          <GroupEditor key={i} refs={refs} g={it} depth={depth + 1} onChange={(x) => set(i, x.items.length ? x : null)} />
        ),
      )}
      <div className="flex flex-wrap gap-2">
        {g.items.length < 10 && (
          <Button type="button" size="sm" variant="ghost" onClick={() => onChange({ ...g, items: [...g.items, newAtom(refs[refs.length - 1]!)] })}>
            <Plus size={14} /> Bedingung
          </Button>
        )}
        {depth === 0 && g.items.length < 10 && (
          <Button type="button" size="sm" variant="ghost" onClick={() => onChange({ ...g, items: [...g.items, { mode: g.mode === 'all' ? 'any' : 'all', items: [newAtom(refs[refs.length - 1]!)] }] })}>
            <Plus size={14} /> Gruppe
          </Button>
        )}
      </div>
    </div>
  );
}

function AtomEditor({ refs, a, onChange }: { refs: Ref[]; a: FormAtom; onChange: (a: FormAtom | null) => void }) {
  const ref = refs.find((r) => r.id === a.q);
  const ops: FormOp[] = ref ? opsForType(ref.type) : ['answered'];
  const t = ref?.type;
  const needsValue = a.op !== 'answered' && a.op !== 'unanswered';
  const multi = a.op === 'in' || a.op === 'containsAny' || a.op === 'containsAll';
  let valueEl: React.ReactNode = null;
  if (needsValue && ref) {
    if (ref.q && (ref.q.type === 'choice' || ref.q.type === 'multichoice')) {
      const cur = Array.isArray(a.value) ? (a.value as string[]) : typeof a.value === 'string' ? [a.value] : [];
      valueEl = multi ? (
        <div className="flex flex-wrap gap-1" role="group" aria-label="Werte">
          {ref.q.options?.map((o) => (
            <label key={o.id} className="flex min-h-9 items-center gap-1.5 rounded-md border border-[var(--line)] px-2 text-sm">
              <input type="checkbox" checked={cur.includes(o.id)} onChange={(e) => onChange({ ...a, value: e.target.checked ? [...cur, o.id] : cur.filter((x) => x !== o.id) })} />
              {o.label}
            </label>
          ))}
        </div>
      ) : (
        <select className={sel} aria-label="Wert" value={cur[0] ?? ''} onChange={(e) => onChange({ ...a, value: e.target.value })}>
          {ref.q.options?.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      );
    } else if (t === 'yesno' || t === 'confirm')
      valueEl = (
        <select className={sel} aria-label="Wert" value={a.value ? '1' : '0'} onChange={(e) => onChange({ ...a, value: e.target.value === '1' })}>
          <option value="1">Ja</option>
          <option value="0">Nein</option>
        </select>
      );
    else {
      const kind = t === 'date' || t === 'datetime' ? 'date' : t === 'number' || t === 'currency' || t === 'calc' || t === 'rating' || t === 'section' ? 'number' : 'text';
      const one = (v: unknown, set: (x: string | number | null) => void, l: string) => (
        <input
          className={cx(sel, 'w-36')}
          aria-label={l}
          type={kind === 'date' ? 'date' : 'text'}
          inputMode={kind === 'number' ? 'decimal' : undefined}
          value={v == null ? '' : String(v).replace('.', kind === 'number' ? ',' : '.')}
          onChange={(e) => {
            const raw = e.target.value;
            if (kind === 'number') {
              const n = Number(raw.replace(',', '.'));
              set(raw === '' ? null : Number.isFinite(n) ? n : null);
            } else set(raw);
          }}
        />
      );
      if (a.op === 'between') {
        const [lo, hi] = Array.isArray(a.value) ? a.value : [null, null];
        valueEl = (
          <span className="flex items-center gap-1 text-sm">
            {one(lo, (x) => onChange({ ...a, value: [x ?? '', hi ?? ''] as [string | number, string | number] }), 'Von')} und{' '}
            {one(hi, (x) => onChange({ ...a, value: [lo ?? '', x ?? ''] as [string | number, string | number] }), 'Bis')}
          </span>
        );
      } else valueEl = one(a.value, (x) => onChange({ ...a, value: x }), 'Wert');
    }
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <select
        className={cx(sel, 'max-w-56')}
        aria-label="Frage"
        value={a.q}
        onChange={(e) => {
          const r = refs.find((x) => x.id === e.target.value)!;
          onChange(newAtom(r));
        }}
      >
        {!ref && <option value={a.q}>Unbekannte Frage</option>}
        {refs.map((r) => (
          <option key={r.id} value={r.id}>
            {r.type === 'section' ? `Anzahl in „${r.label}“` : r.label}
          </option>
        ))}
      </select>
      <select
        className={sel}
        aria-label="Vergleich"
        value={a.op}
        onChange={(e) => {
          const op = e.target.value as FormOp;
          const wasMulti = multi;
          const isMulti = op === 'in' || op === 'containsAny' || op === 'containsAll';
          let value = a.value;
          if (isMulti && !wasMulti) value = typeof a.value === 'string' ? [a.value] : [];
          if (!isMulti && wasMulti) value = Array.isArray(a.value) ? ((a.value[0] as string) ?? null) : a.value;
          if (op === 'between' && !Array.isArray(value)) value = [value as number, value as number];
          if (op !== 'between' && Array.isArray(value) && !isMulti) value = (value[0] as string | number) ?? null;
          onChange({ ...a, op, value });
        }}
      >
        {ops.map((o) => (
          <option key={o} value={o}>
            {formOpLabel(o, t)}
          </option>
        ))}
      </select>
      {valueEl}
      <button type="button" className="inline-flex size-10 items-center justify-center rounded-md text-[var(--ink-soft)] hover:text-rose-600 sm:size-9" aria-label="Bedingung entfernen" onClick={() => onChange(null)}>
        <Trash2 size={15} />
      </button>
    </div>
  );
}
